import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, useFeedback } from "@hawkins/components";

import { ApiError, TranslationNamespace, UserInfoForm } from "../../types";
import { Page } from "../Page";
import { UserInfo, UserInfoRef } from '../forms/UserInfoForm';
import { isApiError, sendApiRequest } from '../../services/sendApiRequest';
import { useNavigate } from "react-router-dom";
import { LoadingOverlay } from "../LoadingOverlay";
import { getUserInfoFromFormValues } from "../../utils";


export default function Register() {
  const { t, i18n } = useTranslation(TranslationNamespace.LEP);
  const userInfoRef = useRef<UserInfoRef>(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useFeedback();
  const navigate = useNavigate();

  const handleSubmit = async (formValues: UserInfoForm) => {
    try {
      setIsSubmitting(true);
      const response: any | ApiError = await sendApiRequest('/register', getUserInfoFromFormValues(formValues, i18n.language));

      let message = "";
      if (isApiError(response)) {
        if (response.status === 409) {
          message = t("error_msg_duplicate");
        } else if (response.status === 403) {
          message = t("error_registration_denied");
        } else if (response.status === 202) {
          navigate("/pending");
        } else {
          message = t("error_msg_2");
        }
        showError(message);
      } else {
        navigate("/submitted", { state: { userInfo: formValues } });
      }
    } catch (error) {
        showError(t("error_msg_2"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    userInfoRef.current?.reset();
  };

  const handleFormSubmit = () => {
    userInfoRef.current?.submit();
  };

  const handleFormStateChange = (isValid: boolean, acceptTerms: boolean) => {
    setCanSubmit(isValid && acceptTerms);
  };

  return (
    <LoadingOverlay
      isLoading={isSubmitting}
      size={60}
    >
      <Page
        showBack backPath="/"
        renderFooterButton={
          () =>
          (<>
            <Button onClick={handleReset} variant="secondary" tabIndex={14}>
              {t("button_reset")}
            </Button>
            <Button onClick={handleFormSubmit} disabled={!canSubmit || isSubmitting} tabIndex={15}>{t("button_submit")}</Button>
          </>)}
      >
        <UserInfo
          onSubmit={handleSubmit}
          onFormStateChange={handleFormStateChange}
          ref={userInfoRef}
        />
      </Page>
    </LoadingOverlay>
  );
}
